import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  ColumnLayout,
  Grid,
  StatusIndicator,
  Link,
  Button,
  Table,
  Spinner,
  BreadcrumbGroup,
  BarChart,
  PieChart,
  Alert,
  Icon,
  Badge,
  SplitPanel,
} from '@cloudscape-design/components';
import axios from 'axios';
import { useAuth } from '../AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import AppLayoutWrapper from './AppLayoutWrapper';

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isEnabled } = useFeatureFlags();
  const [agents, setAgents] = useState([]);
  const [calls, setCalls] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [kbs, setKbs] = useState([]);
  const [telephony, setTelephony] = useState([]);
  const [packs, setPacks] = useState([]);
  const [quotaStatus, setQuotaStatus] = useState({ resources: {} });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const accessToken = user?.access_token || user?.token;
      if (!accessToken) return;

      const [agentsRes, callsRes, wfsRes, kbsRes, sipRes, packsRes, quotaRes] = await Promise.allSettled([
        axios.get('/api/agents/list/', { headers: { Authorization: `Bearer ${accessToken}` } }),
        axios.get('/api/billing/call-details/?limit=100', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => null),
        axios.get('/api/workflows/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => null),
        axios.get('/api/knowledge-bases/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => null),
        axios.get('/api/sip/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => null),
        axios.get('/api/packs/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => null),
        axios.get('/api/billing/quota/', { headers: { Authorization: `Bearer ${accessToken}` } }).catch(() => null),
      ]);

      if (agentsRes.status === 'fulfilled') setAgents(agentsRes.value.data || []);
      if (callsRes.status === 'fulfilled' && callsRes.value) setCalls(callsRes.value.data?.data || []);
      if (wfsRes.status === 'fulfilled') setWorkflows(wfsRes.value.data?.workflows || wfsRes.value.data?.data || []);
      if (kbsRes.status === 'fulfilled') setKbs(kbsRes.value.data?.kbs || kbsRes.value.data?.data || []);
      if (sipRes.status === 'fulfilled') setTelephony(sipRes.value.data?.sip || sipRes.value.data?.data || []);
      if (packsRes.status === 'fulfilled') setPacks(packsRes.value.data?.data || []);
      if (quotaRes.status === 'fulfilled' && quotaRes.value) setQuotaStatus(quotaRes.value.data?.data || { resources: {} });
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const totalAgents = agents.length;
  const voiceAgents = agents.filter((a) => a.ai_agent_type === 'voice').length;
  const textAgents = agents.filter((a) => a.ai_agent_type === 'text' || a.ai_agent_type === 'text_only').length;
  const activeAgents = agents.filter((a) => a.status === 'success' || a.status === 'active').length;
  const totalCalls = calls.length || agents.reduce((sum, a) => sum + (a.call_count || 0), 0);
  const totalWorkflows = workflows.length;
  const totalKbs = kbs.length;
  const totalTelephony = telephony.filter((t) => t.agent_type === 'voice' || t.sip?.phone_number).length;
  const defaultPack = packs.find((p) => p.is_default) || packs[0];
  const inactiveAgents = totalAgents - activeAgents;
  const mcpWorkflows = workflows.filter((w) => w.mcp_enabled).length;
  const activeKbs = kbs.filter((k) => k.is_active !== false).length;
  const activeNumbers = telephony.filter((t) => (t.agent_type === 'voice' || t.sip?.phone_number) && (t.status === 'active' || t.sip?.active === true)).length;

  const resources = quotaStatus.resources || {};
  const voiceMinutes = resources.voice_minutes || {};
  const textConversations = resources.text_conversations || {};
  const documentStorage = resources.document_storage_mb || {};
  const knowledgeBaseQuota = resources.knowledge_bases || {};
  const workflowQuota = resources.workflows || {};

  // Agréger les appels par jour (7 derniers jours)
  const callsByDay = React.useMemo(() => {
    const map = new Map();
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      const key = d.toISOString().split('T')[0];
      map.set(key, { voice: 0, text: 0 });
    }
    // Build agent type lookup from agents data
    const agentTypeMap = new Map();
    agents.forEach((a) => {
      const name = (a.name || '').replace('Assistant - ', '');
      if (a.ai_agent_type) agentTypeMap.set(name, a.ai_agent_type);
    });
    calls.forEach((c) => {
      const date = c.created_at ? c.created_at.split('T')[0] : null;
      if (!date || !map.has(date)) return;
      const agentName = (c.agent_name || '').replace('Assistant - ', '');
      const agentType = agentTypeMap.get(agentName);
      const type = agentType === 'voice' ? 'voice' : 'text';
      const entry = map.get(date);
      entry[type] += 1;
      map.set(date, entry);
    });
    return Array.from(map.entries()).map(([date, counts]) => ({
      date,
      voice: counts.voice,
      text: counts.text,
    }));
  }, [calls, agents]);

  const recentAgents = agents.slice(0, 5);

  const agentStatusData = React.useMemo(() => [
    { title: 'Active', value: activeAgents },
    { title: 'Inactive', value: inactiveAgents },
  ], [activeAgents, inactiveAgents]);

  const agentTypeData = React.useMemo(() => [
    { title: 'Voice', value: voiceAgents },
    { title: 'Text', value: textAgents },
  ], [voiceAgents, textAgents]);

  const formatNumber = (n) => (n === undefined || n === null ? 0 : n);

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[{ text: 'Home', href: '/dashboard' }, { text: 'Dashboard', href: '/dashboard' }]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: 'Dashboard',
        description: 'Overview of your Adexgénie AI agents, quotas, and activity.',
        actions: (
          <Button
            iconName="refresh"
            onClick={fetchData}
            loading={loading}
          >
            Refresh
          </Button>
        ),
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
      helpPanelHeader="Dashboard Overview"
      helpPanelContent={
        <div>
          <p>
            The dashboard gives you a quick overview of your AI agents, call
            activity, quota usage, and system health.
          </p>
          <ul>
            <li>
              <strong>Agents:</strong> Total, voice, text, and active agents
            </li>
            <li>
              <strong>Usage:</strong> Voice minutes, text conversations, storage, KBs, and workflows
            </li>
            <li>
              <strong>Recent agents:</strong> Latest created agents
            </li>
          </ul>
        </div>
      }
      splitPanel={
        <SplitPanel header="Quota details" hidePreferencesButton>
          <SpaceBetween size="m">
            <Box variant="h4">Current plan</Box>
            <Box variant="p">
              {defaultPack ? `${defaultPack.name} — ${defaultPack.currency} ${defaultPack.cycle_price}/${defaultPack.cycle_label || 'month'}` : 'No plan configured'}
            </Box>
            <Box variant="h4">Usage breakdown</Box>
            <ColumnLayout columns={1} variant="text-grid">
              <div>
                <Box variant="small">Voice minutes</Box>
                <Box variant="p">{formatNumber(voiceMinutes.used)} / {formatNumber(voiceMinutes.total)}</Box>
              </div>
              <div>
                <Box variant="small">Text conversations</Box>
                <Box variant="p">{formatNumber(textConversations.used)} / {formatNumber(textConversations.total)}</Box>
              </div>
              <div>
                <Box variant="small">Storage</Box>
                <Box variant="p">{formatNumber(documentStorage.used)} / {formatNumber(documentStorage.total)} MB</Box>
              </div>
              <div>
                <Box variant="small">Knowledge bases</Box>
                <Box variant="p">{formatNumber(knowledgeBaseQuota.used)} / {formatNumber(knowledgeBaseQuota.total)}</Box>
              </div>
              <div>
                <Box variant="small">Workflows</Box>
                <Box variant="p">{formatNumber(workflowQuota.used)} / {formatNumber(workflowQuota.total)}</Box>
              </div>
            </ColumnLayout>
            <Box variant="p">
              <Link href="#" onFollow={() => navigate('/billing')}>Manage billing and quotas</Link>
            </Box>
          </SpaceBetween>
        </SplitPanel>
      }
    >
      {loading ? (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Spinner size="large" />
        </Box>
      ) : (
        <SpaceBetween size="l">
          {/* Welcome / health alert */}
          <Alert
            type="info"
            header={`Welcome back, ${user?.username || 'User'}`}
            action={
              <Button variant="primary" iconName="add-plus" onClick={() => navigate('/Wizard')}>
                Add Agent
              </Button>
            }
          >
            Your workspace is running on plan <strong>{defaultPack ? defaultPack.name : 'N/A'}</strong>.{' '}
            You have {totalAgents} agent{totalAgents !== 1 ? 's' : ''} ({voiceAgents} voice, {textAgents} text) and {totalCalls} interaction{totalCalls !== 1 ? 's' : ''}.
          </Alert>

          {/* Metrics */}
          <Grid
            gridDefinition={[
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
              { colspan: { default: 12, xs: 6, s: 4 } },
            ]}
          >
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="user-profile" variant="normal" /> Total Agents</Box>
                <Box variant="h1" color="text-status-info">
                  {totalAgents}
                </Box>
                <Box variant="small" color="text-status-success">
                  {activeAgents} active {inactiveAgents > 0 && `· ${inactiveAgents} inactive`}
                </Box>
              </ColumnLayout>
            </Container>
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="call" variant="normal" /> Voice Agents</Box>
                <Box variant="h1" color="text-status-info">
                  {voiceAgents}
                </Box>
                <Box variant="small">
                  <Badge color={isEnabled('voice') ? 'grey' : 'red'}>
                    {isEnabled('voice') ? 'Backend ready' : 'Disabled'}
                  </Badge>
                </Box>
              </ColumnLayout>
            </Container>
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="chat" variant="normal" /> Text Agents</Box>
                <Box variant="h1" color="text-status-info">
                  {textAgents}
                </Box>
                <Box variant="small" color={textAgents > 0 ? 'text-status-success' : undefined}>
                  {textAgents > 0 ? 'Active' : 'No text agents'}
                </Box>
              </ColumnLayout>
            </Container>
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="file" variant="normal" /> Knowledge Bases</Box>
                <Box variant="h1" color="text-status-info">
                  {totalKbs}
                </Box>
                <Box variant="small" color={activeKbs > 0 ? 'text-status-success' : undefined}>{activeKbs} active</Box>
              </ColumnLayout>
            </Container>
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="script" variant="normal" /> Workflows</Box>
                <Box variant="h1" color="text-status-info">
                  {totalWorkflows}
                </Box>
                <Box variant="small">{mcpWorkflows} MCP enabled</Box>
              </ColumnLayout>
            </Container>
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="phone" variant="normal" /> Telephony</Box>
                <Box variant="h1" color="text-status-info">
                  {totalTelephony}
                </Box>
                <Box variant="small">
                  <Badge color={isEnabled('telephony') ? 'grey' : 'red'}>
                    {isEnabled('telephony') ? 'Available' : 'Disabled'}
                  </Badge>
                </Box>
              </ColumnLayout>
            </Container>
            <Container>
              <ColumnLayout columns={1} variant="text-grid">
                <Box variant="awsui-key-label"><Icon name="settings" variant="normal" /> Current Plan</Box>
                <Box variant="h1" color="text-status-info">
                  {defaultPack ? defaultPack.name : 'N/A'}
                </Box>
                <Box variant="small">
                  {defaultPack ? `${defaultPack.currency} ${defaultPack.cycle_price}/${defaultPack.cycle_label || 'month'}` : 'No pack configured'}
                </Box>
              </ColumnLayout>
            </Container>
          </Grid>

          {/* Service health */}
          <Container header={<Header variant="h2" description="Real-time status of your Adexgénie workspace resources">Service health</Header>}>
            <ColumnLayout columns={{ default: 1, s: 2, m: 5 }} variant="text-grid">
              <SpaceBetween size="xs">
                <Box variant="small">Agent status</Box>
                <StatusIndicator type={activeAgents > 0 ? 'success' : 'pending'}>
                  {activeAgents > 0 ? `${activeAgents} active` : 'No active agents'}
                </StatusIndicator>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="small">Knowledge base sync</Box>
                <StatusIndicator type={totalKbs > 0 ? 'success' : 'pending'}>
                  {totalKbs > 0 ? `${totalKbs} configured` : 'No KBs'}
                </StatusIndicator>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="small">Telephony</Box>
                <StatusIndicator type={isEnabled('telephony') ? (activeNumbers > 0 ? 'success' : 'pending') : 'stopped'}>
                  {isEnabled('telephony') ? (activeNumbers > 0 ? `${activeNumbers} active` : 'No active numbers') : 'Disabled'}
                </StatusIndicator>
              </SpaceBetween>
              <SpaceBetween size="xs">
                <Box variant="small">Plan</Box>
                <StatusIndicator type={defaultPack ? 'success' : 'warning'}>
                  {defaultPack ? defaultPack.name : 'No plan'}
                </StatusIndicator>
              </SpaceBetween>
            </ColumnLayout>
          </Container>

          {/* Quota cards */}
          <Container header={<Header variant="h2" description="Resource consumption across all active packs" counter={`(${Object.keys(resources).length} resources)`}>Quota usage</Header>}>
            <ColumnLayout columns={{ default: 1, s: 2, m: 5 }} variant="text-grid">
              <div>
                <Box variant="small">Voice minutes</Box>
                <Box variant="h1">
                  {formatNumber(voiceMinutes.used)} / {formatNumber(voiceMinutes.total)}
                </Box>
                <Box variant="small" color={voiceMinutes.used >= voiceMinutes.total ? 'text-status-error' : 'text-status-success'}>
                  {formatNumber(voiceMinutes.remaining)} remaining
                </Box>
              </div>
              <div>
                <Box variant="small">Text conversations</Box>
                <Box variant="h1">
                  {formatNumber(textConversations.used)} / {formatNumber(textConversations.total)}
                </Box>
                <Box variant="small" color={textConversations.used >= textConversations.total ? 'text-status-error' : 'text-status-success'}>
                  {formatNumber(textConversations.remaining)} remaining
                </Box>
              </div>
              <div>
                <Box variant="small">Storage</Box>
                <Box variant="h1">
                  {formatNumber(documentStorage.used)} / {formatNumber(documentStorage.total)} MB
                </Box>
                <Box variant="small" color={documentStorage.used >= documentStorage.total ? 'text-status-error' : 'text-status-success'}>
                  {formatNumber(documentStorage.remaining)} MB remaining
                </Box>
              </div>
              <div>
                <Box variant="small">Knowledge bases</Box>
                <Box variant="h1">
                  {formatNumber(knowledgeBaseQuota.used)} / {formatNumber(knowledgeBaseQuota.total)}
                </Box>
                <Box variant="small" color={knowledgeBaseQuota.used >= knowledgeBaseQuota.total ? 'text-status-error' : 'text-status-success'}>
                  {formatNumber(knowledgeBaseQuota.remaining)} remaining
                </Box>
              </div>
              <div>
                <Box variant="small">Workflows</Box>
                <Box variant="h1">
                  {formatNumber(workflowQuota.used)} / {formatNumber(workflowQuota.total)}
                </Box>
                <Box variant="small" color={workflowQuota.used >= workflowQuota.total ? 'text-status-error' : 'text-status-success'}>
                  {formatNumber(workflowQuota.remaining)} remaining
                </Box>
              </div>
            </ColumnLayout>
          </Container>

          {/* Charts */}
          <Grid
            gridDefinition={[
              { colspan: { default: 12, s: 12, m: 6 } },
              { colspan: { default: 12, s: 6, m: 3 } },
              { colspan: { default: 12, s: 6, m: 3 } },
            ]}
          >
            <Container
              header={<Header variant="h2">Interactions (Last 7 Days)</Header>}
              footer={
                callsByDay.length > 0 && callsByDay.some((d) => d.voice > 0 || d.text > 0) ? (
                  <Box variant="small" color="text-body-secondary">
                    Total: {callsByDay.reduce((sum, d) => sum + d.voice + d.text, 0)} interactions
                  </Box>
                ) : null
              }
            >
              {callsByDay.length > 0 && callsByDay.some((d) => d.voice > 0 || d.text > 0) ? (
                <BarChart
                  series={[
                    {
                      title: 'Voice',
                      type: 'bar',
                      data: callsByDay.map((d) => ({ x: d.date, y: d.voice })),
                    },
                    {
                      title: 'Text',
                      type: 'bar',
                      data: callsByDay.map((d) => ({ x: d.date, y: d.text })),
                    },
                  ]}
                  xDomain={callsByDay.map((d) => d.date)}
                  yDomain={[0, Math.max(1, ...callsByDay.map((d) => Math.max(d.voice, d.text)))]}
                  i18nStrings={{
                    xTickFormatter: (e) =>
                      new Date(e).toLocaleDateString(undefined, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      }),
                  }}
                  ariaLabel="Interactions per day"
                  height={250}
                  hideFilter
                />
              ) : (
                <Box textAlign="center" padding={{ vertical: 'xl' }}>
                  <Box variant="strong" textAlign="center">No interaction data</Box>
                  <Box variant="p" padding={{ bottom: 's' }}>Interactions will appear here once your agents start receiving traffic.</Box>
                  <Button onClick={() => navigate('/Wizard')}>Create an Agent</Button>
                </Box>
              )}
            </Container>

            <Container header={<Header variant="h2">Agent Status</Header>}>
              {totalAgents > 0 ? (
                <PieChart
                  data={agentStatusData}
                  detailPopoverContentDataType="value"
                  segmentDescription={(datum, sum) => `${datum.title}: ${datum.value} of ${sum}`}
                  ariaLabel="Agent status distribution"
                  hideFilter
                  size="medium"
                />
              ) : (
                <Box textAlign="center" padding={{ vertical: 'xl' }}>
                  <Box variant="p" color="text-body-secondary">No agents yet</Box>
                </Box>
              )}
            </Container>

            <Container header={<Header variant="h2">Agent Types</Header>}>
              {totalAgents > 0 ? (
                <PieChart
                  data={agentTypeData}
                  detailPopoverContentDataType="value"
                  segmentDescription={(datum, sum) => `${datum.title}: ${datum.value} of ${sum}`}
                  ariaLabel="Agent type distribution"
                  hideFilter
                  size="medium"
                />
              ) : (
                <Box textAlign="center" padding={{ vertical: 'xl' }}>
                  <Box variant="p" color="text-body-secondary">No agents yet</Box>
                </Box>
              )}
            </Container>
          </Grid>

          {/* Recent Agents */}
          <Container
            header={
              <Header
                variant="h2"
                counter={`(${recentAgents.length})`}
                actions={
                  <Button variant="normal" onClick={() => navigate('/agents')}>
                    View all
                  </Button>
                }
              >
                Recent Agents
              </Header>
            }
          >
            <Table
              variant="embedded"
              trackBy={(item) => item.id || item.adexcloud_agent_id || item.creation_id}
              loadingText="Loading agents"
              columnDefinitions={[
                {
                  id: 'name',
                  header: 'Name',
                  cell: (item) => (
                    <Link href="#" onFollow={() => navigate(`/agents?agent=${item.id || item.adexcloud_agent_id}`)}>
                      {item.name?.replace('Assistant - ', '') || 'Unnamed'}
                    </Link>
                  ),
                },
                {
                  id: 'agent_type',
                  header: 'Type',
                  cell: (item) => (
                    <Badge color={item.ai_agent_type === 'voice' ? 'blue' : 'green'}>
                      {item.ai_agent_type === 'voice' ? 'Voice' : 'Text'}
                    </Badge>
                  ),
                },
                {
                  id: 'type',
                  header: 'Business type',
                  cell: (item) => item.business_type_name || item.type || 'General',
                },
                {
                  id: 'pack',
                  header: 'Pack',
                  cell: (item) => item.pack_name || defaultPack?.name || 'N/A',
                },
                {
                  id: 'status',
                  header: 'Status',
                  cell: (item) => (
                    <StatusIndicator
                      type={
                        item.status === 'success' || item.status === 'active'
                          ? 'success'
                          : item.status === 'pending'
                          ? 'pending'
                          : 'error'
                      }
                    >
                      {item.status || 'Active'}
                    </StatusIndicator>
                  ),
                },
                {
                  id: 'phone',
                  header: 'Phone',
                  cell: (item) => item.phone_number || 'N/A',
                },
                {
                  id: 'calls',
                  header: 'Calls',
                  cell: (item) => item.call_count || 0,
                },
              ]}
              items={recentAgents}
              empty={
                <Box textAlign="center" padding="l">
                  <SpaceBetween size="m">
                    <div>
                      <Box variant="h1" textAlign="center"><Icon name="user-profile" size="big" variant="normal" /></Box>
                      <b>No agents yet</b>
                      <p>Create your first AI agent to start receiving calls and chats.</p>
                    </div>
                    <Button variant="primary" onClick={() => navigate('/Wizard')}>Create your first agent</Button>
                  </SpaceBetween>
                </Box>
              }
            />
          </Container>

          {/* Quick Links */}
          <Container header={<Header variant="h2" description="Quick access to your Adexgénie workspace resources">Resource hubs</Header>}>
            <Grid
              gridDefinition={[
                { colspan: { default: 12, s: 6, m: 3 } },
                { colspan: { default: 12, s: 6, m: 3 } },
                { colspan: { default: 12, s: 6, m: 3 } },
                { colspan: { default: 12, s: 6, m: 3 } },
              ]}
            >
              <Box padding="s">
                <Header variant="h3" description="Manage AI agents and their deployments">
                  <Icon name="user-profile" variant="normal" /> Agents
                </Header>
                <Button onClick={() => navigate('/agents')}>View agents</Button>
              </Box>
              <Box padding="s">
                <Header variant="h3" description="Document-driven answers for agents">
                  <Icon name="file" variant="normal" /> Knowledge Bases
                </Header>
                <Button onClick={() => navigate('/knowledge-bases')}>Manage KBs</Button>
              </Box>
              <Box padding="s">
                <Header variant="h3" description="MCP tools and webhook automation">
                  <Icon name="script" variant="normal" /> Workflows
                </Header>
                <Button onClick={() => navigate('/workflows')}>View workflows</Button>
              </Box>
              <Box padding="s">
                <Header variant="h3" description="Packs and quota usage">
                  <Icon name="payment" variant="normal" /> Billing
                </Header>
                <Button onClick={() => navigate('/billing')}>Open Billing</Button>
              </Box>
            </Grid>
          </Container>
        </SpaceBetween>
      )}
    </AppLayoutWrapper>
  );
}
